import { List, darken } from '@mui/material'
import type { BlockOverlayProps } from 'components/BlockOverlay'
import { GroupCardAvatar } from 'components/cards/styled'
import CalendarSelectPopup, {
  CalendarSectionItem
} from 'components/popups/CalendarSelectPopup'
import { SelectableItem } from 'components/popups/SelectItemWithSearchPopup'
import { ContentProps, prepareCalendars, prepareSections } from './EventPopup'
import PopupSection from 'components/popup/PopupSection'
import PopupSectionRow from 'components/popup/PopupSectionRow'
import { useCalendarSync } from 'components/providers/CalendarSyncProdivder'
import { useNotification } from 'components/providers/NotificationProvider'
import { IEventDetails } from 'hooks/useEventDetails'
import { t } from 'i18next'
import { GroupPrivacy } from 'model/models/Group'
import { useEffect, useState } from 'react'
import { useWatch } from 'react-hook-form'
import Database from 'services/db/Database'
import { localEventByLocalId } from 'services/db/Queries/Event'
import { localGroupById } from 'services/db/Queries/GroupQueries'
import LocalStorage from 'services/LocalStorage'
import Logger from 'services/Logger'
import { theme } from 'theme'
import { DocType, EventDoc, TaskType, UserSettingsDoc } from 'types/api/changes'
import { ICalendar } from 'types/google_events'
import { Model } from 'types/model'
import { LocalStorageKey } from 'types/services/localStorage'
import { defaultConvertedCalendarPhoto } from 'utils/api/groups'
import { is24meApp } from 'utils/appType'
import { CurrentConvertedCalendars } from 'utils/google_events'
import { isConvertedGroup, isConvertedGroupId } from 'utils/groups'
import { removePrefix } from 'utils/object'

import groupDefaultPhoto from '../../../assets/icons/group-default.svg'
import channelDefaultPhoto from '../../../assets/icons/channel_calendar.svg'

export interface CalendarSectionProps extends ContentProps, BlockOverlayProps {
  userSettings: Model<UserSettingsDoc> | UserSettingsDoc
  setGroupID: (id: string) => void
  setThirdPartyId: (id: string) => void
  thirdParty: string
  selectedGroupId?: string
  setType: (type: DocType) => void
  eventHook: IEventDetails
  SupplementaryGroupsIDs?: string[]
  setRegularEventColor?: (color: string) => void
}

export default function CalendarSection(props: CalendarSectionProps) {
  const calendarSync = useCalendarSync()
  const { showNotification } = useNotification()
  const currentGroupIsConvertedCalendar =
    isConvertedGroup(props.group) ||
    props.event.TaskType === TaskType.personalEvent ||
    props.eventHook.getValues('TaskType') === TaskType.personalEvent

  const groupOrCalendarId = props.selectedGroupId

  const [calendarTitle, onCalendarTitle] = useState<string>(
    currentGroupIsConvertedCalendar
      ? calendarSync.calendarTitle(props.thirdParty, props.group)
      : (props.group?.Name ?? '')
  )

  useEffect(() => {
    Logger.blue(
      'EventPOpup:CalendarSection:useEffect',
      props.thirdParty,
      props.selectedGroupId
    )
    if (is24meApp()) {
      localGroupById(props.eventHook.getValues('GroupID') ?? '').then(
        (localGroup) => {
          onCalendarTitle(
            currentGroupIsConvertedCalendar
              ? calendarSync.calendarTitle(props.thirdParty, localGroup)
              : (props.group?.Name ?? '')
          )
        }
      )
    }
  }, [props.thirdParty, props.selectedGroupId])

  const [showCalendarPicker, onShowCalendarPicker] = useState(false)

  const onCalendarPick = (calendar: SelectableItem) => {
    if (props.event._id?.length !== 0) {
      showNotification(t('changeNotSupported'))
      return
    }
    onShowCalendarPicker(false)
    Logger.orange('CalendarSection:', calendar)
    const isSharedCalendar =
      (calendar as ICalendar)?.primaryCalendarEmail !== undefined
    const thirdPartyCalendar = CurrentConvertedCalendars.find(
      (userCalendar) => {
        return userCalendar.id === removePrefix(calendar.id ?? '')
      }
    )
    Logger.orange('CalendarSection:thirdPartyCalendar', thirdPartyCalendar)
    const newGroupId = isSharedCalendar
      ? (calendar.localId ?? calendar.id)
      : calendar.id
    Logger.orange('CalendarSection:calendar', newGroupId)
    Database.write(async () => {
      if (props.event.syncStatus === 'deleted') return
      ;(await localEventByLocalId(props.event.id ?? ''))?.update(
        (event: EventDoc) => {
          Logger.blue('CalendarSection:UpdateColor', thirdPartyCalendar)
          if (thirdPartyCalendar) {
            event.Color =
              darken(thirdPartyCalendar?.backgroundColor ?? '', 0.15) ??
              props.event.Color
            props.eventHook.setSelectedColor(event.Color)
            props.setGroupID('')
          }

          event.ThirdPartyID = `${calendar.id}`
          event.PersonalOwnerId = thirdPartyCalendar
            ? `${(calendar as ICalendar).primaryCalendarEmail}`
            : (LocalStorage.get(LocalStorageKey.USER_ID) ?? '')
        }
      )
    }).then(() => {
      onCalendarTitle(calendar.title)
      if (isSharedCalendar) {
        props.eventHook.setTaskType(TaskType.personalEvent)

        props.eventHook.setReminders([
          ...(props.eventHook
            .getValues('Reminder')
            ?.filter((r) => r.isRelativeReminder === '1') || [])
        ])
      } else {
        props.eventHook.setTaskType(TaskType.meet)
      }
      props.eventHook.setOwnerId(
        thirdPartyCalendar
          ? `${(calendar as ICalendar).primaryCalendarEmail}`
          : (LocalStorage.get(LocalStorageKey.USER_ID) ?? '')
      )
      props.setThirdPartyId(`${calendar.id}`)
      props.setGroupID(newGroupId ?? '')
    })
  }

  const showCalendarPickerForGoogleEvent = () => {
    onShowCalendarPicker(true)
  }

  const [calendars, onCalendars] = useState<SelectableItem[]>([])

  const supplementaryGroupIds =
    useWatch<string[]>({
      control: props.eventHook.control,
      name: 'SupplementaryGroupsIDs',
      defaultValue: props.event.SupplementaryGroupsIDs
    }) ?? []

  //load groups for the list
  useEffect(() => {
    prepareCalendars(props.account, groupOrCalendarId).then((data) => {
      onCalendars(data)
    })
  }, [props.event, props.thirdParty])

  const sections: CalendarSectionItem[] = prepareSections(
    calendars,
    true,
    groupOrCalendarId,
    props.SupplementaryGroupsIDs
  )

  Logger.blue('CalendarSection', groupOrCalendarId)

  const fallbackCalendar = !calendarTitle
    ? calendars.find(
        (c) => c.title && c.id && supplementaryGroupIds.includes(c.id)
      )
    : undefined

  const effectiveTitle = calendarTitle || fallbackCalendar?.title
  const effectiveId = calendarTitle ? groupOrCalendarId : fallbackCalendar?.id
  const effectiveGroup =
    effectiveId === groupOrCalendarId
      ? props.group
      : calendars.find((c) => c.id === effectiveId)?.group

  const existingSupplementaryIds = supplementaryGroupIds
    .filter((groupId) => calendars.find((value) => value.id === groupId))
    .filter((groupId) => groupId !== effectiveId)

  const supplementaryTitles = existingSupplementaryIds
    .map((groupId) => calendars.find((value) => value.id === groupId)?.title)
    .filter((title): title is string => Boolean(title))

  const combinedTitleParts = [effectiveTitle, ...supplementaryTitles].filter(
    (title): title is string => Boolean(title)
  )

  const combinedTitle = combinedTitleParts.join(', ')

  return (
    <CalendarSelectPopup
      group={props.group}
      account={props.account}
      eventHook={props.eventHook}
      onCalendarPick={onCalendarPick}
      calendars={sections}
      open={showCalendarPicker}
      setOpen={onShowCalendarPicker}
      SupplementaryGroupsIDs={props.SupplementaryGroupsIDs ?? []}
    >
      <div style={{ marginTop: 12 }}>
        <PopupSection
          isUiBlocked={props.isUiBlocked}
          padding="0.5rem 0rem 0.8rem 0rem"
          blockedOverlayClick={props.blockedOverlayClick}
          title={`${t('calendar')}`}
        >
          <List disablePadding>
            {combinedTitle && effectiveId && (
              <PopupSectionRow
                key={effectiveId}
                iconClass="icon-big"
                actionBg
                flexGrowForRow={0}
                width="100%"
                valuePlaceholder
                icon={
                  <GroupCardAvatar
                    asGroup
                    img={
                      calendars.find((value) => value.id === effectiveId)?.photo
                    }
                    name=""
                    size={24}
                    emptyPic={
                      isConvertedGroupId(effectiveId)
                        ? defaultConvertedCalendarPhoto(effectiveId)
                        : effectiveGroup?.PrivacyMode === GroupPrivacy.CHANNEL
                          ? (channelDefaultPhoto as any)
                          : (groupDefaultPhoto as any)
                    }
                  />
                }
                title={
                  <div
                    style={{
                      display: 'inline-block',
                      maxWidth: '100%',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      maxHeight: '30px'
                    }}
                  >
                    {combinedTitle}
                  </div>
                }
                overflow="hidden"
                action={showCalendarPickerForGoogleEvent}
                spacing={theme.spacing(0)}
                actionId="change-calendar-button"
              />
            )}
          </List>
        </PopupSection>
      </div>
    </CalendarSelectPopup>
  )
}
